import { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { get_user } from "../../../components/blocks/profileUpdating/dataUpdating/ProfileDataUpdatingBlock";
import Config from "../../../Config";
import { updateSchdeule } from "../../../store/ScheduleSlice";
import { setUser } from "../../../store/UserSlice";
import { buildLessonPaymentStatusMap } from "../../../utilities/getUserLessonStatus";
import { weekDayToFullWeekName } from "../JournalPage";
import { getLessonWordEnding, getWeekRangeForDate } from "./scheduleConstants";
import { useI18n } from "../../../i18n/I18nProvider";

export const useScheduleData = () => {
  const { t } = useI18n();
  const user = useSelector((state) => state.user);
  const schedule = useSelector((state) => state.schedule);
  const student = useSelector((state) => state.student);
  const dispatch = useDispatch();
  const timeZone =
    user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = moment().tz(timeZone);

  const [scheduleWeekRange, setScheduleWeekRange] = useState(
    getWeekRangeForDate(formattedDate),
  );
  const [isLoading, setIsLoading] = useState(true);

  async function loadData() {
    const [lessonsResponse, personalEventsResponse] = await Promise.all([
      axios.get(Config.BACKEND_ADDR + "/lessons/"),
      axios.get(Config.BACKEND_ADDR + "/personal-events/"),
    ]);
    const lessonsData = lessonsResponse.data;
    const personalEventsData = personalEventsResponse.data || [];
    const uniqueStudents = lessonsData
      .map((el) => el.student_id)
      .filter((value, index, array) => array.indexOf(value) === index);
    const studentsBalance = {};

    for (const studentId of uniqueStudents) {
      const studentResponse = await axios.get(
        Config.BACKEND_ADDR + `/students/${studentId}`,
      );
      studentsBalance[studentId] = {
        balance: studentResponse.data.balance,
        cost: studentResponse.data.lesson_cost,
        english_skill: studentResponse.data.english_skill,
        name: studentResponse.data.name,
        image: studentResponse.data.image,
      };
    }
    const paymentStatuses = buildLessonPaymentStatusMap(
      lessonsData,
      studentsBalance,
    );
    const formattedLessons = lessonsData.map((el) => {
      const startTime = moment.utc(el.start_time).tz(timeZone);
      const endTime = moment.utc(el.end_time).tz(timeZone);

      return {
        id: el.id,
        eventType: "lesson",
        studentName: studentsBalance[el.student_id].name,
        studentImage: studentsBalance[el.student_id].image,
        studentId: el.student_id,
        dateDate: startTime.toISOString(),
        endDateDate: endTime.toISOString(),
        timeSlot:
          `${moment(startTime).format("HH:mm")} - ` +
          `${moment(endTime).format("HH:mm")} - ` +
          t("{{count}} занят{{ending}}", {
            count: el.quantity,
            ending: getLessonWordEnding(el.quantity),
          }),
        paymentStatus: paymentStatuses.get(el.id) || "Не оплачено",
        weekDay: weekDayToFullWeekName(el.weekday).short,
        studentLevel: studentsBalance[el.student_id].english_skill,
        note: el.note,
        cost: studentsBalance[el.student_id].cost,
        lessonQuantityText: t("{{count}} занят{{ending}}", {
          count: el.quantity,
          ending: getLessonWordEnding(el.quantity),
        }),
      };
    });
    const personalEventMap = personalEventsData.map((event) => {
      const startTime = moment.utc(event.start_time).tz(timeZone);
      const endTime = moment.utc(event.end_time).tz(timeZone);
      return {
        id: `personal-${event.id}`,
        personalEventId: event.id,
        eventType: "personal",
        title: event.title,
        note: event.note,
        dateDate: startTime.toISOString(),
        endDateDate: endTime.toISOString(),
        timeSlot: `${startTime.format("HH:mm")} - ${endTime.format("HH:mm")}`,
      };
    });
    dispatch(updateSchdeule([...formattedLessons, ...personalEventMap]));
    return true;
  }

  useEffect(() => {
    setScheduleWeekRange(getWeekRangeForDate(formattedDate));
  }, [user.time_zone]);

  useEffect(() => {
    (async () => {
      if (!user || Object.keys(user).length === 0) {
        try {
          const data = await get_user();
          dispatch(setUser(data));
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    })();
  }, [dispatch, user]);

  useEffect(() => {
    loadData();
    setIsLoading(false);
  }, [scheduleWeekRange, user.time_zone]);

  return {
    user,
    student,
    schedule,
    dispatch,
    timeZone,
    scheduleWeekRange,
    setScheduleWeekRange,
    isLoading,
    loadData,
  };
};
